import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useRef, useState} from "react";
import {GetListStatus} from "../../../reduxToolkit/Commons/CommonsApi";
import {getWorkStations} from "../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import HandleChange from "../../../Helpers/HandleChange";
import {BUILDING_FORM_MODEL} from "../Model/Model";
import ButtonIcon from "../../../components/buttons/ButtonIcon";
import {CleanIcon} from "../../../components/buttons/IconButtons";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 * Shared state and handlers of the building form, the create and the update
 * modals render the same inputs and only differ on the dispatched service.
 * @module BuildingFormApp
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} props.show - Controls modal visibility, every change resets the form.
 * @param {string} props.cleanButtonId - Id given to the clear button of the modal header.
 * @param {Function} [props.onReset] - Returns the item the form goes back to, the
 *        default model when it is not given.
 * @param {boolean} [props.useGeolocation=false] - Centers the form on the device
 *        position when the modal opens, only useful while creating.
 * @returns {Object} Returns an object containing:
 * - Localization function `t`
 * - The form model `item` and its generic change handler
 * - UI elements and handlers for the map and its overlay
 */
export const BuildingFormApp = (props) => {
    const {show, cleanButtonId, onReset, useGeolocation = false} = props;

    const {t} = useTranslation();
    // use hook importer
    const dispatch = useDispatch();
    const [emptyFields, setEmptyFields] = useState(false);

    //use Selector hook to get state for redux store
    const {getListStatus} = useSelector((state) => state.CommonsSlice);
    // Zones of the company, the ones the zone select of the form offers
    const employeeWorkStations = useSelector((state) => state?.CompanyEmployeesSlice?.employeeWorkStations);

    //use State hook for local state management
    const [item, setItem] = useState({...BUILDING_FORM_MODEL});
    const {handleChange} = HandleChange({model: item, setModel: setItem});
    const [showOverlay, setShowOverlay] = useState(false); // Overlay visibility state
    const [overlayHiding, setOverlayHiding] = useState(false); // Overlay fade out state
    const [firstClickOnMap, setFirstClickOnMap] = useState(false); // Flag for map click initialization
    const timeoutRef = useRef(null);

    useEffect(() => {
        reset();
    }, [show]);

    // Clear any pending overlay animation timeout on unmount
    useEffect(() => {
        return () => clearTimeout(timeoutRef.current);
    }, []);

    useEffect(() => {
        if (useGeolocation && show && 'geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    setItem((prev) => ({...prev, latitude: lat, longitude: lng}));
                    sessionStorage.setItem('latitude', lat);
                    sessionStorage.setItem('longitude', lng);
                }
            );
        }
    }, [show]);

    // useEffect lifeCyle for dispatch the list of status and the zones
    useEffect(() => {
        dispatch(GetListStatus());
        dispatch(getWorkStations());
    }, []);

    const reset = () => {
        setItem(onReset ? onReset() : {...BUILDING_FORM_MODEL});
        setEmptyFields(false);
        setShowOverlay(true);
        setOverlayHiding(false);
        setFirstClickOnMap(false);
    };

    // Fade the overlay out and remove it once the animation ends
    const overlayAnimation = () => {
        setOverlayHiding(true);
        timeoutRef.current = setTimeout(() => {
            setShowOverlay(false);
            setOverlayHiding(false);
        }, 300);
    };

    const handleMouseEnter = () => {
        overlayAnimation();
    };

    // Bring the overlay back only while the user has not picked a location yet
    const handleMouseLeave = () => {
        setOverlayHiding(false);
        if (!firstClickOnMap) {
            timeoutRef.current = setTimeout(() => {
                setShowOverlay(true);
            }, 300);
        }
    };

    // Map click handler, it also marks the location as already chosen
    const handleMapChange = (name, value) => {
        setFirstClickOnMap(true);
        setItem((prev) => ({...prev, [name]: value}));
    };

    const handleCleanClick = () => {
        setItem((prev) => ({...prev, searchLocation: ''}));
    };

    const clearButton = () => {
        return (
            <ButtonIcon
                id={cleanButtonId}
                Icon={CleanIcon}
                onClick={reset}
                title={'clear_instances'}
                className={"btn-clean-secondary-600"}
                tooltipPlacement={"right"}
                iconProps='btn-icon-svg-12px icon-color-general-00'
            />
        )
    }

    return {
        t,
        item,
        setItem,
        handleChange,
        clearButton,
        handleCleanClick,
        emptyFields,
        setEmptyFields,
        getListStatus,
        employeeWorkStations,
        reset,
        showOverlay,
        overlayHiding,
        handleMouseEnter,
        handleMouseLeave,
        handleMapChange
    }

}
